import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Plus } from 'lucide-react';

import Sidebar from '../layouts/Sidebar';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import DataTable from '../components/ui/DataTable';
import StakeholderService from '../services/StakeholderService';

const TYPES = ['Police', 'Civil Defense', 'Ambulance', 'Hospital', 'Authority', 'Other'];

const EMPTY_FORM = {
  name: '',
  organization: '',
  type: 'Hospital',
  contactPerson: '',
  phone: '',
  email: '',
  address: '',
  notes: '',
  status: 'Active',
};

export default function Stakeholders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const load = async () => {
    try {
      const { data } = await StakeholderService.findAll();
      setRows(data);
    } catch {
      toast.error('Failed to load stakeholders.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setForm({ ...EMPTY_FORM, ...row });
    setEditingId(row.id);
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.organization || !form.type) {
      toast.error('Name, organization and type are required.');
      return;
    }
    try {
      if (editingId) {
        await StakeholderService.update(editingId, form);
        toast.success('Stakeholder updated.');
      } else {
        await StakeholderService.create(form);
        toast.success('Stakeholder added.');
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed.');
    }
  };

  const handleDelete = async () => {
    try {
      await StakeholderService.delete(deleteRow.id);
      toast.success('Stakeholder deleted.');
      setDeleteRow(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed.');
    }
  };

  const headCells = [
    {
      id: 'name',
      label: 'Stakeholder',
      render: (row) => (
        <div>
          <div className="font-bold text-neutral-800 dark:text-neutral-100">{row.name}</div>
          <div className="text-xs text-neutral-500 mt-0.5 dark:text-neutral-500">{row.organization}</div>
        </div>
      ),
    },
    { id: 'type', label: 'Type', render: (row) => <Badge>{row.type}</Badge> },
    { id: 'contactPerson', label: 'Contact Person' },
    {
      id: 'phone',
      label: 'Contact',
      render: (row) => (
        <div className="text-neutral-600 dark:text-neutral-300">
          <div>{row.phone || '—'}</div>
          {row.email && <div className="text-xs text-neutral-400 dark:text-neutral-500">{row.email}</div>}
        </div>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row) => <Badge variant={row.status === 'Active' ? 'green' : 'neutral'}>{row.status}</Badge>,
    },
  ];

  const setField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <Sidebar title="Stakeholders">
      <section className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-bold text-neutral-800 dark:text-neutral-100">Stakeholder Directory</h3>
            <p className="text-xs text-neutral-500 mt-1 dark:text-neutral-500">
              External agencies and internal units coordinated during CBRNE events.
            </p>
          </div>
          <Button icon={Plus} onClick={openCreate}>Add Stakeholder</Button>
        </div>

        <DataTable
          headCells={headCells}
          rows={rows}
          onEdit={openEdit}
          onDelete={(row) => setDeleteRow(row)}
          loading={loading}
          emptyLabel={loading ? 'Loading stakeholders…' : 'No stakeholders registered yet.'}
        />
      </section>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editingId ? 'Edit Stakeholder' : 'Add Stakeholder'}
        subtitle="Coordination contact for incident and drill response."
        confirmLabel={editingId ? 'Save Changes' : 'Add Stakeholder'}
        onConfirm={handleSave}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="form-label" htmlFor="sh-name">Name *</label>
            <input id="sh-name" className="form-input" value={form.name} onChange={setField('name')} />
          </div>
          <div>
            <label className="form-label" htmlFor="sh-org">Organization *</label>
            <input id="sh-org" className="form-input" value={form.organization} onChange={setField('organization')} />
          </div>
          <div>
            <label className="form-label" htmlFor="sh-type">Type *</label>
            <select id="sh-type" className="form-input" value={form.type} onChange={setField('type')}>
              {TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label" htmlFor="sh-contact">Contact Person</label>
            <input id="sh-contact" className="form-input" value={form.contactPerson || ''} onChange={setField('contactPerson')} />
          </div>
          <div>
            <label className="form-label" htmlFor="sh-phone">Phone</label>
            <input id="sh-phone" className="form-input" value={form.phone || ''} onChange={setField('phone')} />
          </div>
          <div>
            <label className="form-label" htmlFor="sh-email">Email</label>
            <input id="sh-email" type="email" className="form-input" value={form.email || ''} onChange={setField('email')} />
          </div>
          <div>
            <label className="form-label" htmlFor="sh-address">Address</label>
            <input id="sh-address" className="form-input" value={form.address || ''} onChange={setField('address')} />
          </div>
          <div>
            <label className="form-label" htmlFor="sh-status">Status</label>
            <select id="sh-status" className="form-input" value={form.status} onChange={setField('status')}>
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="form-label" htmlFor="sh-notes">Notes</label>
            <textarea id="sh-notes" className="form-input" rows={3} value={form.notes || ''} onChange={setField('notes')} />
          </div>
        </div>
      </Modal>

      <Modal
        open={Boolean(deleteRow)}
        onClose={() => setDeleteRow(null)}
        title="Delete Stakeholder"
        subtitle={deleteRow ? `${deleteRow.name} — ${deleteRow.organization}` : ''}
        confirmLabel="Delete"
        onConfirm={handleDelete}
      >
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          This removes the stakeholder from the directory. This action cannot be undone.
        </p>
      </Modal>
    </Sidebar>
  );
}
